/* eslint-disable max-lines */
import {
    Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger
} from 'shared/components/ui/dialog';
import React, {
    useContext, useEffect, useState, useMemo, useCallback
} from 'react';
import {
    ScrollArea
} from 'shared/components/ui/scroll-area';
import {
    Button
} from 'shared/components/ui/button';
import {
    Context
} from 'shared/context/GlobalState';
import {
    Bot, Building2, ChevronDown, ListChecks, ListEnd, MapPinned, ServerCog, BrainCircuit, Eye, Sparkles, AudioLines
} from 'lucide-react';
import {
    Badge
} from 'shared/components/ui/badge';
import {
    FilterBar, SearchBar
} from '../Instructions/Filters';
import {
    Card, CardDescription, CardTitle, CardHeader, CardContent
} from 'shared/components/ui/card';
import {
    Label
} from 'shared/components/ui/label';
import {
    Tabs, TabsContent, TabsList, TabsTrigger
} from 'shared/components/ui/tabs';
import {
    Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from 'shared/components/ui/tooltip';
import {
    buildRankedCatalog
} from '../../classes/modelCategories';

// Category metadata for UI
const categoryConfig = {
    reasoning: {
        label: 'Reasoning',
        icon: BrainCircuit,
        description: 'Models optimized for complex reasoning and problem-solving'
    },
    visual: {
        label: 'Visual',
        icon: Eye,
        description: 'Multimodal models with vision capabilities'
    },
    classical: {
        label: 'Classical',
        icon: Sparkles,
        description: 'General-purpose text models for various tasks'
    },
    audio: {
        label: 'Audio',
        icon: AudioLines,
        description: 'Models for audio transcription and generation. Coming soon!'
    }
};

const CommonModelSelectionModal = ({
    onModelSelect, finalModel, handleReloadModels, modelProviders = []
}) => {
    const [ownedBy, setOwnedBy] = useState('');
    const [provider, setProvider] = useState(null);
    const [contextWindow, setContextWindow] = useState(null);
    const [description, setDescription] = useState('');
    const [region, setRegion] = useState('');
    const [recommendedTasks, setRecommendedTasks] = useState([]);
    const [selectedModel, setSelectedModel] = useState(finalModel || null);
    const [isSelectedModelDeprecated, setIsSelectedModelDeprecated] = useState(false);
    const [selectType, setSelectType] = useState('All');
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCount, setFilterCount] = useState(0);
    const [availableModelTypes, setAvailableModelTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeCategory, setActiveCategory] = useState('reasoning');

    const {
        addNewNotifcation
    } = useContext(Context);

    const onReloadModels = async () => {

        setLoading(true);
        try {
            await handleReloadModels(true);
        } catch (error) {
            console.error('Error reloading models:', error);
            addNewNotifcation('Failed to reload models. Please try again.', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Build categorized and ranked catalog using the categorization logic
    const categorizedModels = useMemo(() => {
        if (!modelProviders || modelProviders.length === 0) {
            return {
                reasoning: [],
                visual: [],
                classical: [],
                audio: []
            };
        }

        // Create a map to deduplicate models - prefer first occurrence (could be by provider priority)
        const uniqueModelsMap = new Map();
        modelProviders.forEach(model => {
            if (!uniqueModelsMap.has(model.model_id)) {
                uniqueModelsMap.set(model.model_id, model);
            }
        });

        // Convert back to array of unique models
        const uniqueModels = Array.from(uniqueModelsMap.values());

        return buildRankedCatalog(uniqueModels);
    }, [modelProviders]);

    // Filter models within the active category
    const filteredCategoryModels = useMemo(() => {
        const categoryModels = categorizedModels[activeCategory] || [];
        const searchTermLower = searchTerm.toLowerCase();
        const isAllType = selectType === 'All';

        // Get full model details for each model ID in the category
        const modelsWithDetails = categoryModels.map(({
            modelId
        }) => {
            // Find all variants of this model (different regions/providers)
            return modelProviders.filter(m => m.model_id === modelId);
        }).flat();

        // Apply search and provider filters
        const filtered = modelsWithDetails.filter((model) => {
            return (
                (model.model_id.toLowerCase().includes(searchTermLower) ||
                    (model.owned_by || '').toLowerCase().includes(searchTermLower)) &&
                (isAllType || model.provider_type === selectType)
            );
        });

        return filtered;
    }, [categorizedModels, activeCategory, searchTerm, selectType, modelProviders]);

    useEffect(() => {
        const uniqueModelTypes = Array.from(new Set(modelProviders.map(model => model.provider_type)));
        setAvailableModelTypes(uniqueModelTypes);

        if (!finalModel?.modelName) return;

        // Optional: use Set if modelProviders is big
        const modelIds = new Set(modelProviders?.map(m => m.model_id));
        const isModelExists = modelIds.has(finalModel.modelName);

        setSelectedModel(prev => {
            if (prev?.modelName !== finalModel.modelName) return finalModel;
            return prev; // avoid re-setting same value
        });

        setIsSelectedModelDeprecated(!isModelExists);
    }, [finalModel?.modelName, modelProviders]);

    useEffect(() => {
        setFilterCount(filteredCategoryModels?.length || 0);
    }, [filteredCategoryModels]);

    const filteredInstructionTypes = [
        {
            id: 1,
            name: 'All',
            value: 'All'
        },
        ...availableModelTypes.map((type, index) => ({
            id: index + 2,
            name: type === 'IBM' ? 'Watson' : type,
            value: type,
        })),
    ];

    const handleModelSelect = (model) => {
        if (selectedModel?.modelName === model.model_id && selectedModel?.modelRegion === model.region) {
            resetModelDetails();
        } else {
            setSelectedModel({
                modelName: model.model_id,
                modelType: model.provider_type,
                modelRegion: model.region || ''
            });
            setOwnedBy(model?.owned_by || '');
            setContextWindow(model?.context_window || 0);
            setRegion(model?.region || 'Not Applicable');
            setProvider(model?.provider_type || '');
            setRecommendedTasks(model?.tasks || []);
            setDescription(model?.description || 'No description available.');
        }
    };

    const resetModelDetails = () => {
        setSelectedModel(null);
        setDescription('');
        setRegion('');
        setRecommendedTasks([]);
    };

    const handleDone = () => {
        if (!selectedModel?.modelName) {
            addNewNotifcation('Please select a model first.', 'warning');
            return;
        }
        onModelSelect(selectedModel);
    };

    // Handle search term change
    const handleSearch = (value) => {
        setSearchTerm(value);
    };

    // Handle search term change
    const handleType = (value) => {
        setSelectType(value);
    };

    const handleCategoryChange = useCallback((value) => {
        if (value === 'audio') return;
        setActiveCategory(value);
    }, []);

    return (
        <Dialog>
            <DialogTrigger className={`${isSelectedModelDeprecated ? 'bg-amber-50 hover:bg-amber-50 text-amber-700 border-amber-300' : ''}`} asChild>
                <Button variant="outline" className="w-full">
                    <ChevronDown className="mr-2 h-4 w-4" />
                    {finalModel?.modelName ? (
                        <span className={`${isSelectedModelDeprecated ? 'line-through' : ''}`}> {finalModel?.modelName} {' - '} <small className='text-muted-foreground'> ({finalModel.modelType}) </small> </span>
                    ) : 'Select a model'}
                </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[90vw] max-h-[90vh] min-h-[300px] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Select a Model</DialogTitle>
                    <DialogDescription>
                        Choose a model by category and see its details.
                    </DialogDescription>
                </DialogHeader>
                {loading ? (
                    <div className="flex items-center justify-center h-full">
                        loading...
                    </div>
                ) : (
                    <div className="space-y-4">
                        <Tabs value={activeCategory} onValueChange={handleCategoryChange} className="w-full">
                            <TabsList className="grid w-full grid-cols-4">
                                {Object.entries(categoryConfig).map(([key, config]) => {
                                    const Icon = config.icon;
                                    const count = categorizedModels[key]?.length || 0;
                                    const isAudioDisabled = key === 'audio';

                                    return (
                                        <TabsTrigger
                                            key={key}
                                            value={key}
                                            aria-disabled={isAudioDisabled}
                                            onClick={
                                                isAudioDisabled ? (event) => {
                                                    event.preventDefault();
                                                    event.stopPropagation();
                                                } : undefined
                                            }
                                            className={`flex items-center justify-center gap-2 ${isAudioDisabled ? 'cursor-not-allowed opacity-60' : ''}`}
                                        >
                                            <TooltipProvider delayDuration={150}>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <span className="flex items-center gap-2">
                                                            <Icon className="h-4 w-4" />
                                                            {config.label}
                                                            <Badge variant="secondary" className="ml-1 h-5 px-1 text-xs">
                                                                {count}
                                                            </Badge>
                                                        </span>
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        <p className="max-w-[220px] text-xs whitespace-pre-wrap">{config.description}</p>
                                                    </TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                        </TabsTrigger>
                                    );
                                })}
                            </TabsList>

                            {Object.keys(categoryConfig).map((category) => (
                                <TabsContent key={category} value={category} className="mt-4">
                                    <div className="flex flex-col md:flex-row gap-4">
                                        <div className="flex flex-col flex-1 min-w-0">
                                            <div className="mb-4 flex flex-col gap-4 p-4 bg-background border rounded-md sm:flex-row sm:items-center sticky top-0 z-10">
                                                <div className="flex-grow">
                                                    <SearchBar onSearch={handleSearch} />
                                                </div>
                                                <FilterBar
                                                    onSearch={handleType}
                                                    selectModelType={selectType}
                                                    types={filteredInstructionTypes}
                                                    placeholder="Filter model type"
                                                />
                                                <div className="flex justify-end w-full sm:w-auto">
                                                    <Button
                                                        disabled={loading}
                                                        onClick={onReloadModels}
                                                    >
                                                        Reload
                                                    </Button>
                                                </div>
                                            </div>
                                            <ScrollArea className="h-[28rem] flex-grow">
                                                <div className="px-4 mb-2">
                                                    <p className='text-sm text-muted-foreground mb-2'>
                                                        {categoryConfig[category].description}
                                                    </p>
                                                    <span className='text-muted-foreground'>
                                                        {filterCount} models found
                                                    </span>
                                                </div>
                                                <div className="grid grid-cols-3 grid-rows-5 gap-4 p-4">
                                                    {filteredCategoryModels.length > 0 ? (
                                                        filteredCategoryModels.map((model, index) => {
                                                            const isSelectedModel = Boolean(
                                                                selectedModel?.modelName === model.model_id &&
                                                                (selectedModel?.modelRegion || '') === (model.region || '') &&
                                                                (selectedModel?.modelType || '') === (model.provider_type || '')
                                                            );

                                                            return (
                                                                <Button
                                                                    key={`${model.model_id}-${model.region || 'default'}-${model.provider_type}-${index}`}
                                                                    variant={
                                                                        isSelectedModel ? 'default' : 'outline'
                                                                    }
                                                                    onClick={() => handleModelSelect(model)}
                                                                    className="justify-start h-auto text-left"
                                                                >
                                                                    <div className="w-full space-y-1">
                                                                        <div className='flex w-full justify-between gap-2 items-center'>
                                                                            <div className="font-semibold w-full truncate">{model.model_id || 'N/A'}</div>
                                                                            <Badge className="px-1 rounded">
                                                                                {model.provider_type || 'N/A'}
                                                                            </Badge>
                                                                        </div>
                                                                        <div className='flex w-full justify-between gap-2 items-center'>
                                                                            <div className="text-xs text-muted-foreground">{model.owned_by || 'N/A'}</div>
                                                                            <div className="text-xs text-muted-foreground">{model.region || ''}</div>
                                                                        </div>
                                                                    </div>
                                                                </Button>
                                                            );
                                                        })
                                                    ) : (
                                                        <p>No models available in this category</p>
                                                    )}
                                                </div>
                                            </ScrollArea>
                                        </div>
                                        {selectedModel?.modelName && (
                                            <div className="md:w-1/3 my-4 min-w-[300px] flex flex-col gap-3">

                                                <Card className="h-[30rem] overflow-y-auto">
                                                    <div className="flex justify-end gap-2 p-4">
                                                        <DialogClose asChild>
                                                            <Button type="button" variant="secondary">
                                                                Close
                                                            </Button>
                                                        </DialogClose>
                                                        <DialogClose className="flex justify-end" asChild>
                                                            <Button
                                                                disabled={
                                                                    finalModel?.modelName === selectedModel?.modelName &&
                                                                    finalModel?.modelRegion === selectedModel?.modelRegion
                                                                }
                                                                variant="default"
                                                                onClick={handleDone}
                                                            >
                                                                Select
                                                            </Button>
                                                        </DialogClose>
                                                    </div>
                                                    <CardHeader>
                                                        <div className="space-y-1">
                                                            <CardTitle className="text-xl whitespace-pre-wrap break-all">{selectedModel?.modelName || ''}</CardTitle>
                                                            <CardDescription className="text-xs">Foundation Model</CardDescription>
                                                        </div>
                                                    </CardHeader>
                                                    <CardContent className="space-y-2.5">
                                                        <div className="space-y-2">
                                                            <div className="flex items-center gap-2 text-xs">
                                                                <Building2 className="h-4 w-4" />
                                                                <Label className="font-medium text-xs">Owned By:</Label>
                                                                <span className="text-muted-foreground">{ownedBy || '-'}</span>
                                                            </div>
                                                            <div className="flex items-center gap-2 text-xs">
                                                                <ListEnd className="h-4 w-4" />
                                                                <Label className="font-medium text-xs">Context Window:</Label>
                                                                <span className="text-muted-foreground">{contextWindow} tokens</span>
                                                            </div>
                                                            <div className="flex items-center gap-2 text-xs">
                                                                <MapPinned className="h-4 w-4" />
                                                                <Label className="font-medium text-xs">Region:</Label>
                                                                <span className="text-muted-foreground">{region || 'Not Applicable'}</span>
                                                            </div>
                                                            <div className="flex items-center gap-2 text-xs">
                                                                <ServerCog className="h-4 w-4" />
                                                                <Label className="font-medium text-xs">Provider:</Label>
                                                                <span className="text-muted-foreground">{provider || 'Not Applicable'}</span>
                                                            </div>
                                                        </div>
                                                        <hr />
                                                        <div className="space-y-2">
                                                            <div className="">
                                                                <Label className="text-xs font-medium">Description</Label>
                                                                <p className="text-xs text-muted-foreground">{description || 'No description available.'}</p>
                                                            </div>
                                                        </div>
                                                        <hr />
                                                        <div className="space-y-2">
                                                            <div className="flex items-center gap-2">
                                                                <Bot className="h-4 w-4" />
                                                                <Label className="text-xs font-medium">Model ID</Label>
                                                            </div>
                                                            <code className="rounded bg-muted px-2 py-1 text-xs whitespace-pre-wrap break-all">{selectedModel?.modelName || '-'}</code>
                                                        </div>
                                                        <hr />
                                                        <div className="space-y-2">
                                                            <div className="flex items-center gap-2">
                                                                <ListChecks className="h-4 w-4" />
                                                                <Label className="text-xs font-medium">Tasks</Label>
                                                            </div>
                                                            <div className="flex flex-wrap gap-2">
                                                                {recommendedTasks && recommendedTasks.length > 0 ? (
                                                                    recommendedTasks.map((task, index) => (
                                                                        <div key={index} className="flex items-center gap-2">
                                                                            <Badge className="bg-muted hover:bg-muted px-1 rounded text-auto">{task}</Badge>
                                                                        </div>
                                                                    ))
                                                                ) : (
                                                                    <p className="text-xs text-muted-foreground">No information about tasks.</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </CardContent>
                                                </Card>
                                            </div>
                                        )}
                                    </div>
                                </TabsContent>
                            ))}
                        </Tabs>
                        {!selectedModel?.modelName && (
                            <div className="flex justify-end">
                                <DialogClose asChild>
                                    <Button type="button" variant="secondary">
                                        Close
                                    </Button>
                                </DialogClose>
                            </div>
                        )}
                    </div>
                )}

            </DialogContent>
        </Dialog>
    );
};

export default CommonModelSelectionModal;